import React, { FC, useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { Table } from "resha"
import { TableHeader } from "../../../../../../../../Common/Table/TableHeader"
import {
    getValue,
    hasValue
} from "../../../../../../../../../Models/Client/FetchState"
import { jobSortOptions } from "../../../../Common/constants"
import {
    decodeFilterAndSort,
    FilterAndSortExpression
} from "../../../../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../../../../Models/Client/SortExpression"
import { NothingMatched } from "../../../../../../../../Common/Empty/NothingMatched"
import { MainThunkDispatch } from "../../../../../../../../../Reducers"
import {
    APIJobListParams,
    APIJobModel,
    APIJobQueryModel,
    decodeJobListParams,
    defaultJobSortExpression,
    defaultParams,
    getJobID,
    JobStatus
} from "../../../../../../../../../Models/API/JobModel"
import { useJobList } from "../../../../../../../../../Hooks/useJobList"
import { jobColumns } from "../../../../JobsList/JobColumns"
import { loadMoreJobList } from "../../../../../../../../../Actions/API/Job/JobActions"
import { Result } from "../../../../../../../../../Models/Utility/Result"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../../../../Routes"
import { useAppInstance } from "../../../../../../../../../Hooks/useAppInstance"
import { JobContentDetailViewer } from "./JobContentDetailViewer"
import { useAssumedRoles } from "../../../../../../../../../Hooks/useAssumedRoles"
import { useJob } from "../../../../../../../../../Hooks/useJob"
import { useAppSchema } from "../../../../../../../../../Hooks/useAppSchema"

export type JobContentPoolParams = Omit<APIJobListParams, "statusIn">

export function decodeJobContentPoolParams(search: URLSearchParams): Result<{
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    params: JobContentPoolParams
}> {
    const filterAndSortDecodeResult = decodeFilterAndSort<APIJobQueryModel>(
        search,
        defaultJobSortExpression
    )
    if (filterAndSortDecodeResult.isError) {
        return filterAndSortDecodeResult
    }

    const decodedParams = decodeJobListParams(filterAndSortDecodeResult.value)
    if (decodedParams.isError) {
        return decodedParams
    }
    return Result.ok({
        filterAndSort: filterAndSortDecodeResult.value,
        params: decodedParams.value
    })
}

interface JobContentPoolProps {
    orgUsername: string
    appID: string
    qualifierID: string
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    params: JobContentPoolParams
    detailedJob?: APIJobModel
    selectedJob?: APIJobModel
    clearSelection: () => void
    setDetailedJob: (job: APIJobModel) => void
    toggleSelection: (job: APIJobModel) => void
    initialSelectJobId?: string | null
}

export const JobContentPool: FC<JobContentPoolProps> = ({
    orgUsername,
    appID,
    qualifierID,
    filterAndSort,
    params = defaultParams,
    detailedJob,
    selectedJob,
    clearSelection,
    setDetailedJob,
    toggleSelection,
    initialSelectJobId
}) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const rolesFetch = useAssumedRoles(appID)
    const appInstance = getValue(useAppInstance(appID))
    const schemaFetch = useAppSchema(appID, false)
    const jobContentPoolParams: JobContentPoolParams = useMemo(
        () => ({ ...params, statusIn: [JobStatus.COMPLETED] }),
        [params]
    )

    const jobsListFetch = useJobList(appID, {
        params: jobContentPoolParams,
        refetchOnStale: true
    })

    const handleLoadMore = useCallback(
        () => dispatch(loadMoreJobList(appID)),
        [appID, dispatch]
    )

    const handleChangeSort = useCallback(
        (sort: SortExpression<APIJobQueryModel>) => {
            clearSelection()
            dispatch(
                push(
                    routePaths.appQualifierTutorialContent(
                        orgUsername,
                        appID,
                        qualifierID,
                        { ...filterAndSort, sort }
                    )
                )
            )
        },
        [
            appID,
            clearSelection,
            dispatch,
            filterAndSort,
            orgUsername,
            qualifierID
        ]
    )

    const columns = useMemo(
        () =>
            jobColumns({
                orgUsername,
                appID,
                activeLearningStatusFetch: Result.ok("unused"),
                sort: filterAndSort?.sort ?? defaultJobSortExpression
            }),
        [appID, filterAndSort, orgUsername]
    )

    const filteredCount = hasValue(jobsListFetch)
        ? jobsListFetch.value.total
        : 0
    const items = hasValue(jobsListFetch)
        ? jobsListFetch.value.items
        : undefined

    const initialSelectedJob = useJob(appID, initialSelectJobId ?? undefined)

    useEffect(() => {
        if (hasValue(initialSelectedJob)) {
            toggleSelection(initialSelectedJob.value)
            setDetailedJob(initialSelectedJob.value)
        }
    }, [initialSelectedJob, setDetailedJob, toggleSelection])

    const renderEmpty = () => <NothingMatched messageVariant="try" />

    if (appInstance === undefined || !hasValue(schemaFetch)) {
        return null
    }
    return (
        <>
            <TableHeader
                sortOptions={{
                    fields: jobSortOptions,
                    applied: filterAndSort?.sort ?? defaultJobSortExpression,
                    onChange: handleChangeSort
                }}
            />
            {detailedJob !== undefined && (
                <JobContentDetailViewer
                    orgUsername={orgUsername}
                    appID={appID}
                    role={getValue(rolesFetch) ?? []}
                    detailedJob={detailedJob}
                    appInstance={appInstance}
                    inputSchema={schemaFetch.value.input}
                    outputSchema={schemaFetch.value.output}
                    filterAndSort={filterAndSort}
                />
            )}
            <div
                className={`flex-1 overflow-auto -m-6 mt-0 px-6 transition-all duration-200 ${
                    detailedJob !== undefined
                        ? "opacity-0 invisible transform -translate-x-full"
                        : ""
                }`}
            >
                <Table
                    columns={columns}
                    getKey={getJobID}
                    checkedItemIds={(selectedJob ? [selectedJob] : []).map(
                        getJobID
                    )}
                    itemCount={filteredCount}
                    items={items}
                    className="w-full h-0 min-h-0 mt-0 table-fixed"
                    loadMore={handleLoadMore}
                    onRowClick={setDetailedJob}
                    renderEmpty={renderEmpty}
                    onToggleRowCheck={toggleSelection}
                />
            </div>
        </>
    )
}
